import {
  FeasibilityQuota,
  FeasibilityQuotaGroupKey,
  FeasibilityQuotaSet,
  generateSurveyQuotaId,
  NESTED_QUOTA_TYPE,
  QUALIFICATION_CODE,
} from '@ps/survey-utils';
import { nonEmptyList } from '@ps/utils';
import { Option } from '@sapphire/result';

import { FeasibilityDefaultQuotaCriteriaDto } from '../dtos/feasibility.dto';

import { FeasibilityQuotaRows } from '../domain';

import { FeasibilityQuotaCriteriaPayloadMapper } from './feasibility-quota-criteria.payload.mapper';

/** The single row a survey with no usable quotas still sends: the defaults, full completes. */
export class FeasibilityDefaultQuotaPayloadMapper {
  constructor(
    private readonly _defaultCriteria: readonly FeasibilityDefaultQuotaCriteriaDto[],
    private readonly _criteriaMapper: FeasibilityQuotaCriteriaPayloadMapper,
    private readonly _completesRequired: number,
  ) {}

  public map(): FeasibilityQuotaRows {
    const buyerQuotaId = generateSurveyQuotaId();

    return {
      quotas: [
        {
          buyer_quota_id: buyerQuotaId,
          criteria: this._criteriaMapper.mapForDefaultQuota(),
          required_count: this._completesRequired,
          quota_type: this._quotaType(),
        },
      ],
      quotaSet: this._toQuotaSet(buyerQuotaId),
    };
  }

  /** Read off the criterion itself rather than assuming the lone one is the appended Age band. */
  private _quotaType(): string {
    return Option.from(
      this._defaultCriteria.length === 1 ? this._defaultCriteria[0] : undefined,
    ).mapOr(NESTED_QUOTA_TYPE, (criterion) =>
      String(criterion.qualification_code),
    );
  }

  private _toQuotaSet(buyerQuotaId: string): FeasibilityQuotaSet {
    const ageCriterion = this._defaultCriteria.find(
      (criterion) => criterion.qualification_code === QUALIFICATION_CODE.AGE,
    );

    return Option.from(ageCriterion).mapOr(
      new Map<FeasibilityQuotaGroupKey, readonly FeasibilityQuota[]>(),
      (criterion) =>
        new Map<FeasibilityQuotaGroupKey, readonly FeasibilityQuota[]>([
          [
            QUALIFICATION_CODE.AGE,
            [this._toDrawerRow(criterion, buyerQuotaId)],
          ],
        ]),
    );
  }

  private _toDrawerRow(
    ageCriterion: FeasibilityDefaultQuotaCriteriaDto,
    buyerQuotaId: string,
  ): FeasibilityQuota {
    return {
      qualificationCode: ageCriterion.qualification_code,
      qualificationName: ageCriterion.qualification_name,
      // Units already resolved before this mapper is constructed.
      ...nonEmptyList(ageCriterion.range_sets).mapOr({}, (rangeSets) => ({
        rangeSets,
      })),
      buyerQuotaId,
      requiredCount: this._completesRequired,
    };
  }
}
